import {
  checkPasswordRequirements,
  generateErrorStringFromError,
  localeNormalizer,
  passwordRegex,
} from '@youshift/shared/utils';
import axios from 'axios';
import { useReducer, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData } from 'react-router-dom';

import SignUpSuccessful from './SignUpSuccessful';

interface FormState {
  firstname: string;
  lastname: string;
  password: string;
  confirmPassword: string;
  has_agreed_to_privacy_policy: boolean;
}

interface LoaderData {
  email: string;
  group_name: string;
  id_group: string;
  id_org: string;
  org_name: string;
}

type ActionType =
  | { type: 'UPDATE_FIELD'; field: keyof FormState; value: string | boolean }
  | { type: 'TOGGLE_PRIVACY_POLICY' }
  | { type: 'RESET' };

const initialState: FormState = {
  firstname: '',
  lastname: '',
  password: '',
  confirmPassword: '',
  has_agreed_to_privacy_policy: false,
};

function reducer(state: FormState, action: ActionType): FormState {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        [action.field]: action.value,
      };
    case 'TOGGLE_PRIVACY_POLICY':
      return {
        ...state,
        has_agreed_to_privacy_policy: !state.has_agreed_to_privacy_policy,
      };
    case 'RESET':
      return {
        ...state,
        firstname: '',
        lastname: '',
        password: '',
        confirmPassword: '',
        has_agreed_to_privacy_policy: false,
      };
    default:
      return state;
  }
}

export default function SignUpUser() {
  const { t, i18n } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [success, setSuccess] = useState<null | string>(null);
  const [isPasswordFieldFocused, setIsPasswordFieldFocused] = useState(false);

  const locale = localeNormalizer(i18n.language);
  const loaderData = useLoaderData() as LoaderData;
  const { email, group_name, id_group, id_org, org_name } = loaderData || {};

  const localeForLegalForms = locale === 'es' ? 'es' : 'en';

  const requirements = checkPasswordRequirements(
    state.password,
    state.confirmPassword,
  );

  const handlePasswordFocus = () => {
    setIsPasswordFieldFocused(true);
  };

  const handlePasswordBlur = () => {
    setIsPasswordFieldFocused(false);
  };

  const signupAction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccess(null);
    const {
      firstname,
      lastname,
      password,
      confirmPassword,
      has_agreed_to_privacy_policy,
    } = state;

    if (!passwordRegex.test(password)) {
      toast.error(t('auth.passwordRequirements'));
    } else if (password !== confirmPassword) {
      toast.error(t('auth.passwordNoMatch'));
    } else if (!has_agreed_to_privacy_policy) {
      toast.error(t('auth.signUpManager.privacyPolicyError'));
    } else {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/user/new`, {
          firstname,
          lastname,
          id_org,
          id_group,
          email,
          password,
          has_agreed_to_privacy_policy,
          language: locale,
        });
        dispatch({ type: 'RESET' });
        setSuccess(t('auth.signUpManager.registrationSuccessUser'));
      } catch (error: unknown) {
        setSuccess(null);
        const errorString = generateErrorStringFromError(
          error,
          t('auth.signUpManager.genericError'),
        );
        toast.error(errorString.errorKey);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    dispatch({ type: 'UPDATE_FIELD', field: name as keyof FormState, value });
  };

  const handleTogglePrivacyPolicy = () => {
    dispatch({ type: 'TOGGLE_PRIVACY_POLICY' });
  };

  return success ? (
    <SignUpSuccessful
      h1={t('auth.signUpManager.successMessage')}
      h2={t('auth.signUpManager.successSubtitle')}
      h3={success}
    />
  ) : (
    <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
      <div className="mx-auto w-full max-w-sm lg:w-96">
        <div>
          <img
            className="h-12 w-auto"
            src={`${process.env.REACT_APP_S3_DOCS_URL}/youshift_logotype.svg`}
            alt="Logo"
          />
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            {t('auth.signUpManager.register')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.signUpManager.or')}{' '}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              {t('auth.signUpManager.loginLink')}
            </Link>
          </p>
        </div>
        <div className="mt-6 -mb-4"></div>
        <form className="space-y-6" onSubmit={signupAction}>
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label
                htmlFor="first-name"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('generic.firstName')}
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="firstname"
                  id="first-name"
                  autoComplete="given-name"
                  value={state.firstname}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label
                htmlFor="last-name"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('generic.lastName')}
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="lastname"
                  id="last-name"
                  autoComplete="family-name"
                  value={state.lastname}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="sm:col-span-full">
              <label
                htmlFor="organization"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('generic.organization')}
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="organization"
                  id="organization"
                  value={org_name}
                  className="block w-full rounded-md border-0 py-1.5 bg-gray-100 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  disabled
                />
              </div>
            </div>

            <div className="col-span-full">
              <label
                htmlFor="team"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('generic.teamName')}
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="group_name"
                  id="team"
                  value={group_name}
                  className="block w-full rounded-md border-0 py-1.5 bg-gray-100 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  disabled
                />
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              {t('generic.email')}
            </label>
            <div className="mt-2">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                className="block w-full bg-gray-100 text-gray-600 rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                disabled
              />
            </div>
          </div>
          <div className="relative grid grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('generic.password')}
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={state.password}
                  onChange={handleChange}
                  onFocus={handlePasswordFocus}
                  onBlur={handlePasswordBlur}
                  className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t('auth.signUpManager.confirmPassword')}
              </label>
              <div className="mt-2">
                <input
                  id="confirm-password"
                  name="confirmPassword"
                  type="password"
                  required
                  value={state.confirmPassword}
                  onChange={handleChange}
                  onFocus={handlePasswordFocus}
                  onBlur={handlePasswordBlur}
                  className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {isPasswordFieldFocused && (
              <div className="absolute left-0 top-full mt-2 bg-white shadow-lg rounded-md p-3 border border-gray-200 z-10">
                <div className="text-sm space-y-1">
                  <div
                    className={`flex items-center ${requirements.length ? 'text-green-600' : 'text-gray-500'}`}
                  >
                    <span className="mr-1">
                      {requirements.length ? '✓' : '○'}
                    </span>
                    {t('auth.signUpManager.passwordRequirements.minLength')}
                  </div>
                  <div
                    className={`flex items-center ${requirements.uppercase ? 'text-green-600' : 'text-gray-500'}`}
                  >
                    <span className="mr-1">
                      {requirements.uppercase ? '✓' : '○'}
                    </span>
                    {t('auth.signUpManager.passwordRequirements.uppercase')}
                  </div>
                  <div
                    className={`flex items-center ${requirements.number ? 'text-green-600' : 'text-gray-500'}`}
                  >
                    <span className="mr-1">
                      {requirements.number ? '✓' : '○'}
                    </span>
                    {t('auth.signUpManager.passwordRequirements.number')}
                  </div>
                  <div
                    className={`flex items-center ${requirements.noSpaces ? 'text-green-600' : 'text-gray-500'}`}
                  >
                    <span className="mr-1">
                      {requirements.noSpaces ? '✓' : '○'}
                    </span>
                    {t('auth.signUpManager.passwordRequirements.noSpaces')}
                  </div>
                  <div
                    className={`flex items-center ${requirements.passwordsMatch ? 'text-green-600' : 'text-gray-500'}`}
                  >
                    <span className="mr-1">
                      {requirements.passwordsMatch ? '✓' : '○'}
                    </span>
                    {t(
                      'auth.signUpManager.passwordRequirements.passwordsMatch',
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="privacy-policy"
                name="privacyPolicy"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                checked={state.has_agreed_to_privacy_policy}
                onChange={handleTogglePrivacyPolicy}
              />
              <label
                htmlFor="privacy-policy"
                className="ml-2 block text-sm text-gray-900"
              >
                {t('auth.signUpManager.privacyPolicyLabel')}{' '}
                <a
                  href={`${process.env.REACT_APP_S3_DOCS_URL}/legal/${localeForLegalForms}/privacy_policy_users_${localeForLegalForms}.pdf`}
                  className="text-blue-600"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('auth.signUpManager.privacyPolicyLink')}
                </a>
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-blue-600 py-2 px-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              {t('auth.signUpManager.createAccount')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
